import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  ITranslationMap,
  SourceEntityRecordStatus,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IBrand,
  IOrganisation,
  IPractice,
  IStaffer,
  ServiceTypeModality,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshotCombineLatest,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BaseStafferDestinationEntity,
  IStafferMigrationData,
} from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  PracticeWorksStaffer,
  StafferSourceEntity,
} from '../../source/entities/staff';
import { PracticeWorksStafferMappingHandler } from '../mappings/staff';
import { PracticeWorkStaffToUserMappingHandler } from '../mappings/staff-to-user';
import { User } from '@principle-theorem/principle-core';
import {
  resolveExactStaffLocation,
  StaffToPracticeMapping,
} from '../../../exact/destination/mappings/practitioner-to-practice-mapping';

export const STAFF_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'staff',
    label: 'Staff',
    description: '',
  },
});

interface IStafferJobData {
  stafferRecord: IGetRecordResponse<PracticeWorksStaffer>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  organisation: WithRef<IOrganisation>;
  brand: WithRef<IBrand>;
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}

export class StafferDestinationEntity extends BaseStafferDestinationEntity<PracticeWorksStaffer> {
  destinationEntity = STAFF_DESTINATION_ENTITY;
  stafferSourceEntity = new StafferSourceEntity();
  stafferCustomMapping = new PracticeWorksStafferMappingHandler();
  staffToUserCustomMapping = new PracticeWorkStaffToUserMappingHandler();

  override sourceEntities = {
    staff: new StafferSourceEntity(),
  };

  customMappings = {
    staff: new PracticeWorksStafferMappingHandler(),
    staffToUser: new PracticeWorkStaffToUserMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  override buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IStafferJobData[]> {
    const staff$ = this.stafferCustomMapping.getRecords(translationMapHandler);
    const organisation$ = PracticeMigration.organisation$(migration);
    const brand$ = PracticeMigration.brand$(migration);
    const staffToPractice$ = this.customMappings.staffToPractice.getRecords(
      translationMapHandler
    );

    return snapshotCombineLatest([
      organisation$,
      brand$,
      staff$,
      staffToPractice$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.stafferSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([staffRecords, [organisation, brand, staff, staffToPractice]]) =>
        staffRecords.map((stafferRecord) => ({
          stafferRecord,
          organisation,
          brand,
          staff,
          staffToPractice,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IStafferJobData
  ): Promise<
    | IStafferMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    if (data.stafferRecord.record.status === SourceEntityRecordStatus.Invalid) {
      return this._buildErrorResponse(
        data.stafferRecord.record,
        'Source staffer is invalid'
      );
    }

    const sourceStafferId = this.sourceEntities.staff.getSourceRecordId(
      data.stafferRecord.data
    );
    const practice = await resolveExactStaffLocation(
      sourceStafferId,
      translationMap,
      data.staffToPractice,
      migration.configuration.practices
    );
    if (!practice) {
      return this._buildErrorResponse(
        data.stafferRecord.record,
        'Staffer location could not be resolved'
      );
    }

    const name = `${data.stafferRecord.data.firstName ?? ''} ${
      data.stafferRecord.data.surname ?? ''
    }`.trim();

    const user = User.init({
      name: name.length ? name : data.stafferRecord.data.id,
      email: '',
      practices: [practice.ref],
      brands: [migration.configuration.brand.ref],
      isEnabled: false,
    });

    return {
      sourceStafferId,
      stafferDetails: {
        providerNumber: data.stafferRecord.data.licenseNumber || '',
        providerModality: ServiceTypeModality.GeneralDentist,
      },
      user,
    };
  }
}
